import React, { useEffect, useState, useCallback } from 'react';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ListingsMap from './ListingsMap';
import './AdminDashboard.css';

interface DashboardStats {
  totalUsers: number;
  newUsersToday: number;
  totalOffers: number;
  offersToday: number;
  totalListings: number;
  activeListings: number;
  pendingLenders: number;
  avgPTCScore: number;
  userGrowth: number; // percentage
  offerGrowth: number; // percentage
}

interface ChartData {
  name: string;
  value: number;
  [key: string]: string | number; // Index signature for recharts compatibility
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    newUsersToday: 0,
    totalOffers: 0,
    offersToday: 0,
    totalListings: 0,
    activeListings: 0,
    pendingLenders: 0,
    avgPTCScore: 0,
    userGrowth: 0,
    offerGrowth: 0
  });
  const [userGrowthData, setUserGrowthData] = useState<ChartData[]>([]);
  const [offerTrendData, setOfferTrendData] = useState<ChartData[]>([]);
  const [ptcScoreDistribution, setPtcScoreDistribution] = useState<ChartData[]>([]);
  const [parsingStatusData, setParsingStatusData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = Timestamp.fromDate(today);

      // Calculate date range
      const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const rangeStart = new Date();
      rangeStart.setDate(rangeStart.getDate() - daysAgo);
      const rangeTimestamp = Timestamp.fromDate(rangeStart);

      // Fetch users
      const usersSnapshot = await getDocs(collection(db, 'userProfiles'));
      const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const newUsersToday = users.filter((u: any) => {
        if (!u.createdAt) return false;
        const createdDate = u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
        return createdDate >= today;
      }).length;

      // Fetch offers
      const offersSnapshot = await getDocs(collection(db, 'offers'));
      const offers = offersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const offersToday = offers.filter((o: any) => {
        if (!o.submissionDate) return false;
        const submissionDate = o.submissionDate.toDate ? o.submissionDate.toDate() : new Date(o.submissionDate);
        return submissionDate >= today;
      }).length;

      // Calculate average PTC score
      // Check if ptcScore is a number directly or an object with totalScore
      const offersWithPTC = offers.filter((o: any) => {
        const hasScore = typeof o.ptcScore === 'number' ? o.ptcScore > 0 : o.ptcScore?.totalScore > 0;
        return hasScore;
      });
      
      // PTC score filtering complete
      
      const avgPTC = offersWithPTC.length > 0
        ? offersWithPTC.reduce((sum: number, o: any) => {
            let score = 0;
            if (typeof o.ptcScore === 'number') {
              score = o.ptcScore;
            } else if (o.ptcScore?.totalScore) {
              score = o.ptcScore.totalScore;
            }
            
            // Smart normalization to 0-100 range
            // Scores can be stored in multiple formats:
            // - 0.0 to 1.0 (decimal) -> multiply by 100
            // - 1 to 100 (percentage) -> use as is
            // - Some edge cases like 0.04 vs 4 vs 40
            
            if (score > 0 && score <= 1) {
              // Decimal format (0.4, 0.49, 0.38) -> convert to percentage
              score = score * 100;
            } else if (score > 1 && score <= 100) {
              // Already in percentage format (40, 49, 38) -> use as is
            }
            
            return sum + score;
          }, 0) / offersWithPTC.length
        : 0;
      
      const avgPTCScore = avgPTC;

      // PTC Score Distribution
      const ptcRanges = [
        { name: '90-100', min: 90, max: 100, count: 0 },
        { name: '80-89', min: 80, max: 89, count: 0 },
        { name: '70-79', min: 70, max: 79, count: 0 },
        { name: '60-69', min: 60, max: 69, count: 0 },
        { name: '<60', min: 0, max: 59, count: 0 }
      ];

      offersWithPTC.forEach((o: any) => {
        let score = 0;
        if (typeof o.ptcScore === 'number') {
          score = o.ptcScore;
        } else if (o.ptcScore?.totalScore) {
          score = o.ptcScore.totalScore;
        }
        
        // Smart normalization to 0-100 range (same logic as average calculation)
        if (score > 0 && score <= 1) {
          // Decimal format -> convert to percentage
          score = score * 100;
        } else if (score > 1 && score <= 100) {
          // Already in percentage format -> use as is
        }
        
        const range = ptcRanges.find(r => score >= r.min && score <= r.max);
        if (range) range.count++;
      });

      // Fetch listings
      const listingsSnapshot = await getDocs(collection(db, 'listings'));
      const listings = listingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const activeListings = listings.filter((l: any) => l.status === 'active').length;

      // Fetch pending lenders
      const lendersSnapshot = await getDocs(collection(db, 'lenders'));
      const pendingLenders = lendersSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.verificationStatus === 'pending' || !data.verified;
      }).length;

      // Calculate growth rates (comparing to previous period)
      const prevPeriodStart = new Date(rangeStart);
      prevPeriodStart.setDate(prevPeriodStart.getDate() - daysAgo);
      const prevPeriodTimestamp = Timestamp.fromDate(prevPeriodStart);

      const usersInCurrentPeriod = users.filter((u: any) => {
        if (!u.createdAt) return false;
        const createdDate = u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
        return createdDate >= rangeStart;
      }).length;

      const usersInPrevPeriod = users.filter((u: any) => {
        if (!u.createdAt) return false;
        const createdDate = u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
        return createdDate >= prevPeriodStart && createdDate < rangeStart;
      }).length;

      const userGrowth = usersInPrevPeriod > 0
        ? ((usersInCurrentPeriod - usersInPrevPeriod) / usersInPrevPeriod) * 100
        : 100;

      const offersInCurrentPeriod = offers.filter((o: any) => {
        if (!o.submissionDate) return false;
        const submissionDate = o.submissionDate.toDate ? o.submissionDate.toDate() : new Date(o.submissionDate);
        return submissionDate >= rangeStart;
      }).length;

      const offersInPrevPeriod = offers.filter((o: any) => {
        if (!o.submissionDate) return false;
        const submissionDate = o.submissionDate.toDate ? o.submissionDate.toDate() : new Date(o.submissionDate);
        return submissionDate >= prevPeriodStart && submissionDate < rangeStart;
      }).length;

      const offerGrowth = offersInPrevPeriod > 0
        ? ((offersInCurrentPeriod - offersInPrevPeriod) / offersInPrevPeriod) * 100
        : 100;

      // Prepare chart data - User Growth
      const usersByDay = prepareTimeSeriesData(users, 'createdAt', daysAgo);
      
      // Prepare chart data - Offer Trends
      const offersByDay = prepareTimeSeriesData(offers, 'submissionDate', daysAgo);

      const newStats = {
        totalUsers: users.length,
        newUsersToday,
        totalOffers: offers.length,
        offersToday,
        totalListings: listings.length,
        activeListings,
        pendingLenders,
        avgPTCScore: avgPTCScore,
        userGrowth: Math.round(userGrowth),
        offerGrowth: Math.round(offerGrowth)
      };

      setStats(newStats);

      setUserGrowthData(usersByDay);
      setOfferTrendData(offersByDay);
      
      // Only set PTC distribution if there are offers with scores
      const ptcDistribution = ptcRanges.map(r => ({ name: r.name, value: r.count }));
      setPtcScoreDistribution(ptcDistribution);

      // Parsing Status Distribution
      const parsingStatusCounts = {
        completed: offers.filter((o: any) => o.parsingStatus === 'completed').length,
        failed: offers.filter((o: any) => o.parsingStatus === 'failed').length,
        processing: offers.filter((o: any) => o.parsingStatus === 'processing').length,
        pending: offers.filter((o: any) => !o.parsingStatus || o.parsingStatus === 'pending').length
      };

      const parsingData = [
        { name: 'Successful', value: parsingStatusCounts.completed },
        { name: 'Failed', value: parsingStatusCounts.failed },
        { name: 'Processing', value: parsingStatusCounts.processing },
        { name: 'Pending', value: parsingStatusCounts.pending }
      ].filter(d => d.value > 0);

      setParsingStatusData(parsingData);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const prepareTimeSeriesData = (items: any[], dateField: string, days: number): ChartData[] => {
    const data: { [key: string]: number } = {};
    const today = new Date();
    
    // Initialize all days with 0
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      data[key] = 0;
    }

    // Count items per day
    items.forEach((item: any) => {
      if (!item[dateField]) return;
      const itemDate = item[dateField].toDate ? item[dateField].toDate() : new Date(item[dateField]);
      const key = itemDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (data.hasOwnProperty(key)) {
        data[key]++;
      }
    });

    return Object.entries(data).map(([name, value]) => ({ name, value }));
  };

  const COLORS = ['#0891b2', '#06b6d4', '#22d3ee', '#67e8f9', '#a5f3fc'];
  const PARSING_COLORS = {
    Successful: '#10b981',
    Failed: '#ef4444',
    Processing: '#f59e0b',
    Pending: '#6b7280'
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p className="dashboard-subtitle">Business analytics and key performance indicators</p>
        </div>
        <div className="time-range-selector">
          <button 
            className={timeRange === '7d' ? 'active' : ''} 
            onClick={() => setTimeRange('7d')}
          >
            7 Days
          </button>
          <button 
            className={timeRange === '30d' ? 'active' : ''} 
            onClick={() => setTimeRange('30d')}
          >
            30 Days
          </button>
          <button 
            className={timeRange === '90d' ? 'active' : ''} 
            onClick={() => setTimeRange('90d')}
          >
            90 Days
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-main">
            <div className="stat-left">
              <div className="stat-header">
                <div className="stat-icon" style={{ background: '#dbeafe' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="stat-label">Total Users</div>
              </div>
            </div>
            <div className="stat-value">{stats.totalUsers.toLocaleString()}</div>
          </div>
          <div className="stat-footer">
            <div className={`stat-trend ${stats.userGrowth >= 0 ? 'positive' : 'negative'}`}>
              <span className="trend-icon">{stats.userGrowth >= 0 ? '↑' : '↓'}</span>
              {Math.abs(stats.userGrowth)}% from previous period
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-main">
            <div className="stat-left">
              <div className="stat-header">
                <div className="stat-icon" style={{ background: '#d1fae5' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                  </svg>
                </div>
                <div className="stat-label">Total Offers</div>
              </div>
            </div>
            <div className="stat-value">{stats.totalOffers.toLocaleString()}</div>
          </div>
          <div className="stat-footer">
            <div className={`stat-trend ${stats.offerGrowth >= 0 ? 'positive' : 'negative'}`}>
              <span className="trend-icon">{stats.offerGrowth >= 0 ? '↑' : '↓'}</span>
              {Math.abs(stats.offerGrowth)}% from previous period
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-main">
            <div className="stat-left">
              <div className="stat-header">
                <div className="stat-icon" style={{ background: '#fef3c7' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2">
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                </div>
                <div className="stat-label">Active Listings</div>
              </div>
            </div>
            <div className="stat-value">{stats.activeListings.toLocaleString()}</div>
          </div>
          <div className="stat-footer">
            <div className="stat-detail">{stats.totalListings} total</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-main">
            <div className="stat-left">
              <div className="stat-header">
                <div className="stat-icon" style={{ background: '#e9d5ff' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7e22ce" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 6v6l4 2"></path>
                  </svg>
                </div>
                <div className="stat-label">Pending Lenders</div>
              </div>
            </div>
            <div className="stat-value">{stats.pendingLenders.toLocaleString()}</div>
          </div>
          <div className="stat-footer">
            <div className="stat-detail">Awaiting verification</div>
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-main">
            <div className="stat-left">
              <div className="stat-header">
                <div className="stat-icon" style={{ background: '#fce7f3' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#be185d" strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                </div>
                <div className="stat-label">Avg PTC Score</div>
              </div>
            </div>
            <div className="stat-value">{stats.avgPTCScore.toFixed(1)}%</div>
          </div>
          <div className="stat-footer">
            <div className="stat-detail">Average score</div>
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-main">
            <div className="stat-left">
              <div className="stat-header">
                <div className="stat-icon" style={{ background: '#dcfce7' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <div className="stat-label">New Today</div>
              </div>
            </div>
            <div className="stat-value">{stats.newUsersToday + stats.offersToday}</div>
          </div>
          <div className="stat-footer">
            <div className="stat-detail">{stats.newUsersToday} users, {stats.offersToday} offers</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        {/* User Growth Chart */}
        <div className="chart-card">
          <h3>User Growth Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip 
                contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#0891b2" 
                strokeWidth={2}
                name="New Users"
                dot={{ fill: '#0891b2', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Offer Trends Chart */}
        <div className="chart-card">
          <h3>Offer Submission Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={offerTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip 
                contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px' }}
              />
              <Legend />
              <Bar dataKey="value" fill="#0891b2" name="Offers" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* PTC Score Distribution */}
        <div className="chart-card">
          <h3>PTC Score Distribution</h3>
          {ptcScoreDistribution.some(d => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={ptcScoreDistribution.filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {ptcScoreDistribution.filter(d => d.value > 0).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data-message">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>No PTC scores available yet</p>
              <span>Scores will appear once offers with PTC calculations are submitted</span>
            </div>
          )}
        </div>

        {/* PDF Parsing Status */}
        <div className="chart-card">
          <h3>PDF Parsing Status</h3>
          {parsingStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={parsingStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {parsingStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PARSING_COLORS[entry.name as keyof typeof PARSING_COLORS] || '#6b7280'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data-message">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="12" y1="18" x2="12" y2="12"></line>
                <line x1="9" y1="15" x2="15" y2="15"></line>
              </svg>
              <p>No PDF parsing data available</p>
              <span>Data will appear once offer PDFs are processed</span>
            </div>
          )}
        </div>
      </div>

      {/* Listings Map */}
      <div className="map-section">
        <h3>Listings Map</h3>
        <ListingsMap height="500px" />
      </div>
    </div>
  );
};

export default AdminDashboard;
